import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../../shared/components/Icons';
import { formatCharge } from '../../shared/data/common';
import { useBookings } from '../../shared/booking/BookingsContext';
import { cancelBus, getBusBooking, quoteBusCancellation } from '../../api/bus';
import { useAuth } from '../../auth/AuthContext';
import { cancellationLines } from './data';

const statusText = {
  Confirmed: ['Confirmed', 'live'],
  Completed: ['Travelled', ''],
  Cancelled: ['Cancelled', 'cancelled'],
  ServiceCancelled: ['Cancelled by operator', 'cancelled'],
};

/** Live ticket details from the operator, for bookings made through the live API. */
export function useLiveBus(booking, attempt = 0) {
  const { auth } = useAuth();
  const [state, setState] = useState({ loading: Boolean(booking?.supplierBookingId), details: null, error: '' });
  const id = booking?.supplierBookingId;
  const email = booking?.contact?.email;
  const token = auth?.accessToken;
  useEffect(() => {
    if (!id) return undefined;
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: '' }));
    getBusBooking(id, email, token, controller.signal)
      .then((details) => setState({ loading: false, details, error: '' }))
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setState({
          loading: false,
          details: null,
          error: error.status === 404
            ? 'We could not find this ticket with the operator for these details.'
            : error.message || 'Unable to load the latest ticket details.',
        });
      });
    return () => controller.abort();
  }, [id, email, token, attempt]);
  return state;
}

/** Live status of a bus ticket: operator PNR, passengers and, when `allowCancel`, cancelling with the operator. */
export default function BusTrip({ booking, allowCancel = false }) {
  const { auth } = useAuth();
  const { pathname } = useLocation();
  const { cancelBooking } = useBookings();
  const [attempt, setAttempt] = useState(0);
  const state = useLiveBus(booking, attempt);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [done, setDone] = useState('');
  const supplierBookingId = booking?.supplierBookingId;
  const token = auth?.accessToken;

  // Keep My Trips in step with the operator: cancelled by support, or the trip cancelled by the operator.
  const liveStatus = state.details?.status;
  useEffect(() => {
    if (!booking || booking.status === 'cancelled') return;
    if (liveStatus === 'Cancelled') cancelBooking(booking.id, booking.refund || { amount: state.details.refundAmount || 0, note: 'Cancelled with the bus operator.' });
    if (liveStatus === 'ServiceCancelled') cancelBooking(booking.id, { amount: booking.fare.total, note: 'The operator cancelled this trip, so the full amount is refunded.' });
  }, [liveStatus, booking, cancelBooking, state.details]);

  if (!supplierBookingId) return null;

  const heading = <h3><Icon name="bus" size={18} /> Your ticket</h3>;
  if (state.loading && !state.details) {
    return <section className="panel ins-policies" role="status">{heading}<p className="muted">Fetching the latest from the bus operator…</p></section>;
  }
  if (state.error) {
    return (
      <section className="panel ins-policies">
        {heading}
        <p className="text-coral" role="alert">{state.error}</p>
        <button type="button" className="btn btn-outline" onClick={() => setAttempt(attempt + 1)}>Try again</button>
      </section>
    );
  }

  const { details } = state;
  const [label, pillClass] = statusText[details.status] || [details.status, ''];
  const live = details.status === 'Confirmed';
  const canCancel = allowCancel && live && details.canCancel;

  const getQuote = async () => {
    setBusy(true);
    setActionError('');
    setDone('');
    try {
      setQuote(await quoteBusCancellation(supplierBookingId, token));
    } catch (error) {
      setActionError(error.message || 'We could not get a cancellation quote.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setActionError('');
    try {
      const result = await cancelBus(supplierBookingId, quote.cancellationId, token);
      cancelBooking(booking.id, { amount: result.customerRefund, note: 'Operator cancellation charges apply; the convenience fee is non-refundable.' });
      setDone(`Cancelled. ${formatCharge(result.customerRefund)} will be refunded to your original payment method within 5–7 working days.`);
      setQuote(null);
      setAttempt((n) => n + 1);
    } catch (error) {
      setActionError(error.status === 409
        ? error.message
        : `${error.message || 'The bus operator did not confirm the cancellation.'} Your ticket is still booked — please contact support if you need to cancel.`);
      setQuote(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel ins-policies">
      <div className="fbs-head">
        {heading}
        <span className={`status-pill ${pillClass}`}>{label}</span>
      </div>
      <div className="fbs-ids">
        <div><span className="muted small">Operator PNR</span><b>{details.operatorPnr || 'Shared by the operator'}</b></div>
        <div><span className="muted small">{booking.supplierCode || details.supplierCode} ticket no.</span><b>{details.supplierBookingId}</b></div>
        {details.departureTime && <div><span className="muted small">Departure</span><b>{details.departureTime}</b></div>}
        <div><span className="muted small">Amount charged</span><b>{formatCharge(details.totalAmount)}</b></div>
      </div>

      {details.status === 'ServiceCancelled' && (
        <div className="api-message error" role="alert">The operator cancelled this trip. The full amount will be refunded to your original payment method.</div>
      )}

      {details.passengers.length > 0 && (
        <table className="data-table">
          <thead><tr><th>Passenger</th><th>Seat</th><th>Age / gender</th></tr></thead>
          <tbody>
            {details.passengers.map((p) => (
              <tr key={`${p.seatId}-${p.name}`}><td><b>{p.name}</b></td><td>{p.seatId}</td><td className="muted small">{[p.age, p.gender].filter(Boolean).join(' · ') || '—'}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      {details.operatorContact && <p className="muted small"><Icon name="phone" size={13} /> Operator: {details.operatorContact}</p>}

      {live && (
        <div className="btn-row ride-actions">
          <button type="button" className="btn btn-outline" onClick={() => setAttempt(attempt + 1)} disabled={state.loading}>
            {state.loading ? 'Refreshing…' : 'Refresh status'}
          </button>
        </div>
      )}

      {done && <div className="api-message success" role="status">{done}</div>}
      {actionError && <div className="api-message error" role="alert">{actionError}</div>}

      {allowCancel && live && (
        <div className="ins-cancel">
          {details.cancellationPolicy.length > 0 && (
            <ul className="policy-list small-list">{cancellationLines(details.cancellationPolicy).map((l) => <li key={l}>{l}</li>)}</ul>
          )}
          {!canCancel ? (
            <p className="muted small">This ticket can no longer be cancelled online. Contact support for help.</p>
          ) : !token ? (
            <p className="muted small">
              <Link className="link" to={`/account/login?returnTo=${encodeURIComponent(pathname)}`}>Sign in</Link> with {booking.contact.email} to cancel this ticket online.
            </p>
          ) : (
            <button type="button" className="btn btn-danger" disabled={busy} onClick={getQuote}>
              {busy && !quote ? 'Checking refund…' : 'Cancel ticket'}
            </button>
          )}
        </div>
      )}

      {quote && (
        <div className="overlay" onClick={() => !busy && setQuote(null)}>
          <div className="overlay-card modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Cancel ticket">
            <h3>Cancel this ticket?</h3>
            <p className="muted">All seats on the ticket will be cancelled.</p>
            <div className="refund-box">
              <div className="summary-line"><span>Amount paid</span><span>{formatCharge(details.totalAmount)}</span></div>
              <div className="summary-line"><span>Operator cancellation charges</span><span>{formatCharge(quote.cancellationCharges)}</span></div>
              <div className="summary-line total"><span>Refund</span><b className="text-primary">{formatCharge(quote.customerRefund)}</b></div>
              <p className="muted small">Credited to your original payment method within 5–7 working days. The convenience fee is not refunded. This cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" disabled={busy} onClick={() => setQuote(null)}>Keep ticket</button>
              <button type="button" className="btn btn-danger" disabled={busy} onClick={confirm}>{busy ? 'Cancelling…' : 'Yes, cancel'}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
